import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import SwiperCore from "swiper";

import { couponApi, merchantApi } from "@api";
import { ComponentCouponDetail, ComponentPopUpConfirm, ComponentPopUpDone } from "@components";
import { enumStatus, enumTypes, enumValidation, PATH_404, PATH_HOME } from "@configs";
import { ICoupon, IRewardUsed } from "@interfaces";
import { selectAuth, selectConfigClient, setCouponId, setExternalTypes, setLoading } from "@redux";
import CacheStore from "@Rocket-Innovation/components/store/CacheStore";
import UserStore from "@Rocket-Innovation/components/store/UserStore";
import { formatMessage, useNotify } from "@utils";
import LoadingOverlay from "react-loading-overlay-ts";
import { deduplicate } from "src/Utils/requestDeduper";

export const PageCoupon = () => {
    //page hook
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id, idParent, alienCode } = useParams<{
        id: string;
        idParent: string;
        alienCode: string;
    }>();
    const history = useHistory();
    const { error, success, warning } = useNotify();
    //redux state
    const { auth, userInfo } = useSelector(selectAuth);
    const configClient = useSelector(selectConfigClient);
    //page state
    const [coupon, setCoupon] = useState<ICoupon>();
    const [rewardUsed, setRewardUsed] = useState<IRewardUsed>();
    const [currentImage, setCurrentImage] = useState<number>(enumValidation.NUMBER_ONE);
    const [showPopupDone, setShowPopupDone] = useState<boolean>(false);
    const [showPopupConfirm, setShowPopupConfirm] = useState<boolean>(false);
    const [cNote, setCNote] = useState<string>("");
    const [isActive, setActive] = useState<boolean>(false);

    useEffect(() => {
        const controller = new AbortController();
        useCoupon(controller.signal);
        console.log("auth ===>", auth);
        console.log("UserStore ===>", UserStore.getState().accessToken);
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (idParent) {
            fetchPersonalCoppon();
        }
    }, [idParent]);

    const fetchPersonalCoppon = async () => {
        try {
            dispatch(setLoading(false));
            if (idParent) {
                const res = await deduplicate(`personal-coupon-${idParent}`, () =>
                    merchantApi.getPersonalCoupon(idParent)
                );
                setCNote(res.data.note);
            }
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            }
            history.push("/");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const useCoupon = async (signal?: AbortSignal) => {
        try {
            dispatch(setLoading(true));
            if (id) {
                const res = await deduplicate(`coupon-${id}`, () => merchantApi.getCouponId(id));
                if (signal?.aborted) return;
                const data = res.data as ICoupon;
                setCoupon(data);
                const index = configClient.findIndex((item) => item.merchantId === data.merchantId);
                if (index < 0 || userInfo?.status === enumStatus.INACTIVE) {
                    dispatch(setExternalTypes({ type: enumTypes.COUPON, id }));
                } else {
                    dispatch(setExternalTypes(null));
                }
            } else {
                history.push("/");
            }
        } catch (err: any) {
            if (err.response) {
                error(formatMessage(err.response));
            }
            history.push("/");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleReceiveCoupon = async () => {
        await null;
        // checkIsLogin();
        if (!checkIsLogin()) {
            try {
                dispatch(setLoading(true));
                setActive(true);
                const res = await couponApi.receiveCoupon({
                    objectId: coupon?._id || "",
                    status: enumStatus.ACTIVE,
                    type: enumTypes.COUPON,
                });
                if (res) {
                    success(formatMessage(res));
                }
                history.push(PATH_HOME);
            } catch (err: any) {
                if (err.response) {
                    error(formatMessage(err.response));
                } else {
                    error(t("message.received.duplicate"));
                }
            } finally {
                dispatch(setLoading(false));
                setActive(false);
            }
        }
    };

    const handleRedeemedCoupon = async () => {
        try {
            if (idParent) {
                dispatch(setLoading(true));
                setActive(true);
                const res = await couponApi.redeemedCoupon({
                    objectId: coupon?._id || "",
                    status: enumStatus.ACTIVE,
                    type: enumTypes.COUPON,
                    id: idParent,
                });
                setRewardUsed(res.data.coupon);
                if (res) {
                    success(formatMessage(res));
                }
            } else {
                dispatch(setLoading(true));
                setActive(true);
                const res = await couponApi.redeemedCouponNotId(
                    {
                        objectId: coupon?._id || "",
                        status: enumStatus.ACTIVE,
                        type: enumTypes.COUPON,
                    },
                    id || ""
                );
                setRewardUsed(res.data.coupon);
                if (res) {
                    success(formatMessage(res));
                }
            }
            setShowPopupDone(true);
            history.push(PATH_HOME);
        } catch (errors: any) {
            if (errors.response) {
                error(formatMessage(errors.response));
            } else {
                error(t("message.received.duplicate"));
            }
        } finally {
            dispatch(setLoading(false));
            setActive(false);
        }
    };

    const handleUseCoupon = () => {
        checkIsLogin();
        setShowPopupConfirm(true);
    };

    const handleChangeImage = (swiper: SwiperCore) => {
        setCurrentImage(swiper.realIndex + 1);
    };

    const checkIsLogin = () => {
        if (!auth?.accessToken && !UserStore.getState().accessToken) {
            dispatch(setCouponId(coupon?._id || ""));

            //Update in Rocket-Innovation's components
            CacheStore.setState({
                appendFields: {
                    ...CacheStore.getState().appendFields,
                    couponId: coupon?._id || "",
                },
            });

            warning(t("page.please_sign_up_to_collect_coupon"));
            history.push(PATH_HOME);
            return true;
        } else if (userInfo?.status === enumStatus.INACTIVE) {
            history.push(PATH_HOME);
            return true;
        } else {
            if (coupon?.isNewMemberOnly) {
                warning(t("page.for_new_member_only"));
                history.push(PATH_HOME);
                return true;
            } else {
                return false;
            }
        }
    };

    const handleClosePopupDone = () => {
        setShowPopupDone(false);
        history.push(PATH_HOME);
    };

    const handleClosePopupConfirm = () => {
        setShowPopupConfirm(false);
    };

    return (
        <LoadingOverlay active={isActive} spinner text="Loading...">
            {coupon && (
                <ComponentCouponDetail
                    couponCollected={!!idParent}
                    handleChangeImage={handleChangeImage}
                    handleUseCoupon={handleUseCoupon}
                    coupon={coupon}
                    alienCode={alienCode}
                    handleReceiveCoupon={handleReceiveCoupon}
                    currentImage={currentImage}
                    cNote={cNote}
                />
            )}
            {showPopupConfirm && coupon && (
                <ComponentPopUpConfirm
                    handleCloseDetail={handleClosePopupConfirm}
                    data={coupon as ICoupon}
                    handleConfirm={handleRedeemedCoupon}
                    cNote={cNote}
                />
            )}
            {showPopupDone && coupon && (
                <ComponentPopUpDone
                    handleCloseDetail={handleClosePopupDone}
                    data={coupon as ICoupon}
                    usedData={rewardUsed}
                    cNote={cNote}
                />
            )}
        </LoadingOverlay>
    );
};
